import { useNavigate } from "react-router-dom";

import { calcAge, formatDob, formatGender } from "./formatMember";
import type { MemberSearchResult } from "./graphql/memberSearch";

interface Props {
  results: MemberSearchResult[];
  loading: boolean;
  hasSearched: boolean;
  searchFilters?: SearchFilters;
}

export interface SearchFilters {
  firstName: string;
  lastName: string;
  mpi: string;
  dob: string;
}

export function MemberResultsTable({ results, loading, hasSearched, searchFilters }: Props) {
  const navigate = useNavigate();

  if (loading) {
    return (
      // Skeleton height tracks the real row (~68px), not the old nested-card
      // row it replaced — a loading state that reserves 24 units of height and
      // then settles into 17 makes the whole list jump on every search.
      <div className="flex flex-col gap-2" aria-busy="true" aria-live="polite">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[68px] w-full rounded-md bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  if (!hasSearched) {
    return (
      <div className="text-center py-16 px-6 rounded-2xl border border-dashed border-border bg-card/50">
        <p className="text-base font-medium text-foreground">Search for a member to get started</p>
        <p className="text-sm text-muted-foreground mt-1">
          Enter a first and last name (2+ characters each), or a Member ID.
        </p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-16 text-base text-muted-foreground font-medium">
        No members found. Try adjusting your search.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground font-medium">
        {results.length} result{results.length !== 1 ? "s" : ""}
      </p>
      {/* Divider list, not a stack of nested cards. These rows sit INSIDE a
          card already (the Home widget's `Card`, the page's own surface), so
          giving each result its own border, radius and hover-shadow boxed a
          box and spent ~40px of vertical space per row on chrome. A hairline
          between rows carries the same separation for one pixel. */}
      <ul className="flex flex-col list-none m-0 p-0 divide-y divide-border">
        {results.map((m) => {
          const age = calcAge(m.dob);
          const plan = m.healthplanName ?? m.healthplanCode;
          // PP member search has no line-of-business field, so hide the LOB
          // line for PP results rather than show a meaningless "—".
          const isPp = m._id.startsWith("pp:");
          return (
            <li key={m._id}>
              <button
                type="button"
                onClick={() => {
                  // P6d multi-tab shell — carry the name we already have in
                  // hand as `tabTitle` so the opened/focused patient tab gets
                  // a real title immediately instead of the placeholder (see
                  // TabbedAppShell.tsx / profileTabs.ts). Read-only extra key
                  // alongside the existing state fields; a no-op for the
                  // classic (non-shell) route.
                  const tabTitle = m.name ?? undefined;
                  if (m._id.startsWith("pp:")) {
                    // Open the full Member 360 profile (Documents is one of its
                    // tabs), not the bare Documents page. The `_id` contains
                    // ':' so URL-encode it; the PP member fields ride along in
                    // location.state for the profile to build its patient.
                    navigate(`/members/${encodeURIComponent(m._id)}`, {
                      state: {
                        ppMember: m._pp,
                        ...(searchFilters ? { searchFilters } : {}),
                        ...(tabTitle ? { tabTitle } : {}),
                      },
                    });
                  } else {
                    navigate(`/members/${m._id}`, {
                      state:
                        searchFilters || tabTitle
                          ? { ...(searchFilters ? { searchFilters } : {}), ...(tabTitle ? { tabTitle } : {}) }
                          : undefined,
                    });
                  }
                }}
                className="w-full text-left px-3 py-2.5 rounded-md hover:bg-accent/50 focus-visible:bg-accent/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors flex items-center justify-between gap-3 group"
              >
                <div className="flex flex-col gap-1 min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                      {m.name ?? "—"}
                    </span>
                    {/* Identity line: DOB first, then age and sex as one
                        cluster — "02/20/1961 · 65 y/o F". DOB used to sit in
                        the meta grid below, but it is the field people scan to
                        confirm they have the right patient, so it belongs
                        beside the name rather than a line down. The single dot
                        separates the date from the age/sex pair; age and sex
                        read as one phrase and take no dot between them. */}
                    <span className="text-sm text-muted-foreground">
                      {formatDob(m.dob)} ·{age !== undefined ? ` ${age} y/o` : ""}{" "}
                      {formatGender(m.gender)}
                    </span>
                    {/* Both pills in ONE flex item, so they move to the next
                        line TOGETHER instead of the IPA chip clinging to the
                        identity line while the longer plan name drops alone.
                        `shrink-0` is what forces the unit wrap: without it the
                        group shrinks to its widest pill and wraps internally on
                        line one. `max-w-full` is the escape hatch — if even a
                        full row can't hold both, the group's own `flex-wrap`
                        stacks them rather than overflowing the row. */}
                    {(m.ipaCode || plan) && (
                      <span className="flex shrink-0 max-w-full flex-wrap items-center gap-2">
                        {m.ipaCode && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 ring-1 ring-inset ring-blue-200 dark:ring-blue-700/50">
                            IPA: {m.ipaCode}
                          </span>
                        )}
                        {plan && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 ring-1 ring-inset ring-violet-200 dark:ring-violet-700/50">
                            {plan}
                          </span>
                        )}
                      </span>
                    )}
                  </div>

                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm m-0">
                    <MetaRow label="MPI" value={m.memberPolicyId ?? "—"} mono />
                    {!isPp && <MetaRow label="LOB" value={m.lineOfBusiness ?? "—"} />}
                  </dl>
                </div>
                <span
                  aria-hidden="true"
                  className="text-muted-foreground text-lg group-hover:text-primary transition-colors shrink-0"
                >
                  ›
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function MetaRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground shrink-0">
        {label}
      </dt>
      <dd
        className={
          "m-0 text-sm text-foreground truncate " + (mono ? "font-mono" : "font-medium")
        }
        title={value}
      >
        {value}
      </dd>
    </div>
  );
}
